import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { FaBookmark, FaUndo, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';

export default function FormikDraftPersist({ storageKey = 'ishop_purchase_form_draft', isEdit = false }) {
  const { values, setValues, resetForm } = useFormikContext();
  const [draftBanner, setDraftBanner] = useState(null);

  // 1. Check for existing saved draft on initial render
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.data && Array.isArray(parsed.data.purchasedItems)) {
          const validItems = parsed.data.purchasedItems.filter(i => i && (i.item_id || i.itemName));
          if (validItems.length > 0 || parsed.data.supplier || parsed.data.referenceNumber || parsed.data.supplier_bill_no) {
            setDraftBanner(parsed);
          }
        }
      }
    } catch (e) {
      console.warn("Error reading purchase draft:", e);
    }
  }, [storageKey, isEdit]);

  // 2. Real-time auto-save on EVERY form change (keystroke / item addition / serial edit)
  useEffect(() => {
    if (isEdit) return;
    if (!values) return;

    const validItems = (values.purchasedItems || []).filter(i => i && (i.item_id || i.itemName));
    const hasSubstantialData = validItems.length > 0 || Boolean(values.supplier) || Boolean(values.referenceNumber) || Boolean(values.supplier_bill_no);

    if (hasSubstantialData) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          data: values,
          savedAt: new Date().toISOString(),
          itemCount: validItems.length
        }));
      } catch (e) {
        console.warn("Purchase draft auto-save failed:", e);
      }
    }
  }, [values, isEdit, storageKey]);

  // 3. Navigation Guard: Warn user before tab close/refresh if unsaved items exist
  useEffect(() => {
    if (isEdit) return;
    const handleBeforeUnload = (e) => {
      const validItems = (values?.purchasedItems || []).filter(i => i && (i.item_id || i.itemName));
      if (validItems.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have an unsaved purchase draft. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [values, isEdit]);

  if (!draftBanner || isEdit) return null;

  // Restore saved draft into Formik state
  const handleRestore = () => {
    if (draftBanner.data) {
      setValues(draftBanner.data);
      setDraftBanner(null);
      toast.success("Draft restored! All items and serial numbers loaded.");
    }
  };

  // Completely purge draft from localStorage
  const handleDiscard = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {}
    setDraftBanner(null);
    toast.info("Draft completely purged from storage.");
  };

  return (
    <div className="alert alert-info border border-info shadow-sm p-3 mb-4 rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div className="d-flex align-items-center gap-3">
        <div className="p-2 rounded bg-info text-white">
          <FaBookmark size={18} />
        </div>
        <div>
          <strong className="d-block text-dark" style={{ fontSize: '14px' }}>
            Unsaved Purchase Draft Detected
          </strong>
          <small className="text-secondary">
            Auto-saved from {new Date(draftBanner.savedAt).toLocaleString()} ({draftBanner.itemCount || 0} items filled)
          </small>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-info text-white fw-bold px-3 d-flex align-items-center gap-1"
          onClick={handleRestore}
        >
          <FaUndo size={12} /> Restore Draft
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary px-3 d-flex align-items-center gap-1"
          onClick={handleDiscard}
        >
          <FaTrash size={12} /> Discard & Clear
        </button>
      </div>
    </div>
  );
}

/**
 * Utility function to manually purge draft storage key
 */
export function purgePurchaseDraft(storageKey) {
  try {
    localStorage.removeItem(storageKey);
  } catch (e) {
    console.warn("Failed to purge draft:", e);
  }
}
